import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Card, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link, useParams } from "react-router-dom";
import { IoMdRemoveCircle } from "react-icons/io";
import { updateActivity, getActivity } from "../../store/slices/activity";
import { getPlace } from "../../store/slices/place";
import { FaTrash, FaImage } from "react-icons/fa";
import { uploadImage } from "../../utils/helpers/fileupload";
import { toast } from "react-toastify";
import { getVendor } from "../../store/slices/vendor";
import Select from "react-select";

const generateTimeSlots = () => {
  const slots = [];
  let start = 2 * 60;
  const end = 24 * 60;

  while (start <= end) {
    const hours = Math.floor(start / 60);
    const minutes = start % 60;

    const formattedHours = hours.toString().padStart(2, "0");
    const formattedMinutes = minutes.toString().padStart(2, "0");

    const label = `${formattedHours}:${formattedMinutes}`;

    slots.push({ value: label, label });

    start += 30;
  }

  return slots;
};
const TIME_OPTIONS = generateTimeSlots();

export default function EditActivity() {
  const { id } = useParams();

  const { placeList = [] } = useSelector((state) => state.place);

  const { vendorList } = useSelector((state) => state.vendor);

  const vendors = vendorList?.vendors || [];

  const [loading, setLoading] = useState(false);

  const user = JSON.parse(localStorage.getItem("user"));
  const token = user?.data?.accessToken;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    description: "",
  });

  const [locationform, setlocationForm] = useState({
    locations: [
      {
        placeId: "",
        price: "",
        vendor: "",
        timings: [],
      },
    ],
  });

  useEffect(() => {
    dispatch(getPlace());
  }, [dispatch]);

  useEffect(() => {
    const item = { service: "ACTIVITY" };
    dispatch(getVendor(item));
  }, [dispatch]);

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState({});

  const [locationErrors, setLocationErrors] = useState([]);

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        setLoading(true);

        const res = await dispatch(getActivity({ id })).unwrap();

        const data = res?.activity?.[0];

        if (!data) return;

        setForm({
          name: data.name || "",
          description: data.description || "",
        });

        const formattedLocations = data.locations?.map((loc) => ({
          placeId: loc.placeId,
          vendor: loc.vendor?._id,
          price: String(loc.price),
          timings: loc.timings || [],
        })) || [{ placeId: "", price: "", vendor: "", timings: [] }];

        setlocationForm({
          locations: formattedLocations,
        });

        if (data.image) {
          setPreview(data.image);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchActivity();
    }
  }, [id, dispatch]);

  const validateLocations = () => {
    let isValid = true;
    const updatedErrors = [];

    locationform.locations.forEach((loc, index) => {
      const errors = {};

      if (!loc.placeId) {
        errors.placeId = "Please select place";
        isValid = false;
      }

      if (!loc.price) {
        errors.price = "Please enter price";
        isValid = false;
      }
      if (!loc.vendor) {
        errors.vendor = "Please select vendor";
        isValid = false;
      }

      updatedErrors[index] = errors;
    });

    setLocationErrors(updatedErrors);

    return isValid;
  };

  const addLocationRow = () => {
    setlocationForm((prev) => ({
      ...prev,
      locations: [
        ...prev.locations,
        { placeId: "", price: "", vendor: "", timings: [] },
      ],
    }));
  };

  const removeLocationRow = (index) => {
    const updated = [...locationform.locations];
    updated.splice(index, 1);
    setlocationForm({
      ...locationform,
      locations: updated,
    });
  };

  const handleTimingChange = (index, selectedOptions) => {
    const updated = [...locationform.locations];

    updated[index].timings = selectedOptions
      ? selectedOptions.map((opt) => opt.value)
      : [];

    setlocationForm({
      ...locationform,
      locations: updated,
    });
  };

  const handleLocationChange = (index, field, value) => {
    const updated = [...locationform.locations];
    const updatedErrors = [...locationErrors];

    if (!updatedErrors[index]) {
      updatedErrors[index] = {};
    }

    if (field === "price") {
      value = value.replace(/[^0-9]/g, "");
      value = value.slice(0, 5);

      const numeric = Number(value);

      if (!numeric || numeric <= 0 || numeric > 20000) {
        updatedErrors[index].price = "Please enter price between 1 to 20000";
      } else {
        updatedErrors[index].price = "";
      }
    }

    /*  CLEAR PLACE ERROR */
    if (field === "placeId") {
      if (!value) {
        updatedErrors[index].placeId = "Please select place";
      } else {
        updatedErrors[index].placeId = "";
      }
    }

    /*  FIX: CLEAR VENDOR ERROR */
    if (field === "vendor") {
      if (!value) {
        updatedErrors[index].vendor = "Please select vendor";
      } else {
        updatedErrors[index].vendor = "";
      }
    }

    updated[index][field] = value;

    setlocationForm({
      ...locationform,
      locations: updated,
    });

    setLocationErrors(updatedErrors);
  };

  const nameRegex = /^[a-zA-Z0-9][\s\S]*$/;

  const handleInputChange = ({ target: { name, value } }) => {
    let errorMsg = "";

    if (name === "name") {
      if (value && !nameRegex.test(value)) {
        errorMsg = "Only letters allowed";
      } else if (!value) {
        errorMsg = "Please enter activity name";
      } else if (value.length > 100) {
        errorMsg = "Maximum 100 characters allowed";
      }
    }

    if (name === "price") {
      const numericValue = value.replace(/[^0-9]/g, "");
      setForm((prev) => ({ ...prev, price: numericValue }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        image: "Image must be less than 5MB",
      }));
      return;
    }

    setImage(file);
    setPreview(URL.createObjectURL(file));
    setErrors((prev) => ({ ...prev, image: "" }));
  };

  const removeImage = () => {
    setImage(null);
    setPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = {};

    // 🔹 Validate main fields FIRST
    if (!form.name.trim()) {
      newErrors.name = "Please enter activity name";
    }

    if (!form.description.trim()) {
      newErrors.description = "Please enter description";
    }

    const isLocationValid = validateLocations();

    if (Object.keys(newErrors).length > 0 || !isLocationValid) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);

      let imageUrl = preview;

      if (image) {
        imageUrl = await uploadImage(image, token);
      }

      const finalLocations = locationform.locations
        .filter((loc) => loc.placeId && loc.price && loc.vendor)
        .map((loc) => ({
          place: loc.placeId,
          price: Number(loc.price),
          vendor: loc.vendor,
          timings: loc.timings || [],
        }));

      if (!finalLocations.length) {
        toast.error("At least one valid location is required");
        return;
      }

      const payload = {
        id,
        name: form.name.trim(),
        description: form.description.trim(),
        image: imageUrl,
        locations: finalLocations,
      };

      await dispatch(updateActivity(payload)).unwrap();

      toast.success("Activity updated successfully");
      navigate("/admin/activity");
    } catch (error) {
      toast.error(error || "Failed to update activity");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="breadcrumb-bar mb-3">
        <div className="breadcrumb-left">
          <Link to="/admin/home" className="breadcrumb-link active">
            Home
          </Link>
          |
          <Link to="/admin/activity" className="breadcrumb-link active">
            Activity
          </Link>
          | <span className="breadcrumb-link inactive">EditActivity</span>
        </div>
      </div>

      <Container fluid className="p-4">
        <Card className="shadow-sm border-0 rounded-3">
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Row>
                {/* Activity Name */}
                <Col md={5} className="mb-3">
                  <Form.Label>Activity Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleInputChange}
                    maxLength={100}
                    placeholder="Enter activity name "
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name}</small>
                  )}
                </Col>

                <Col md={5}>
                  <Form.Group className="mb-3">
                    <Form.Label>Image</Form.Label>

                    <div
                      className="custom-file-uploader"
                      onClick={() =>
                        document.getElementById("imageUpload").click()
                      }
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files[0];
                        handleImageChange({ target: { files: [file] } });
                      }}
                    >
                      {!preview ? (
                        <div className="upload-placeholder">
                          <div className="upload-icon-box">
                            <FaImage className="upload-icon-white" />
                          </div>
                          <p className="mb-1">Click or Drag Image Here</p>
                        </div>
                      ) : (
                        <div className="image-preview-wrapper">
                          <img
                            src={preview}
                            alt="preview"
                            className="preview-img"
                          />
                          <IoMdRemoveCircle
                            size={24}
                            className="remove-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImage();
                            }}
                          />
                        </div>
                      )}

                      <Form.Control
                        id="imageUpload"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        hidden
                      />
                    </div>

                    {errors.image && (
                      <small className="text-danger d-block mt-2">
                        {errors.image}
                      </small>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                {/* Description */}
                <Col md={10} className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={form.description}
                    onChange={handleInputChange}
                    placeholder="Enter description"
                  />
                  {errors.description && (
                    <small className="text-danger">{errors.description}</small>
                  )}
                </Col>
              </Row>

              <Row className="align-items-end mt-3">
                <Col
                  md={11}
                  className="d-flex justify-content-between align-items-center"
                >
                  <h5 className="mb-0">Place and Price</h5>

                  <small style={{ color: "red" }} className="my-2">
                    {errors.error}
                  </small>

                  <button
                    type="button"
                    className="submit-btn"
                    style={{ width: "40px" }}
                    onClick={addLocationRow}
                  >
                    +
                  </button>
                </Col>

                {locationform.locations.map((loc, index) => (
                  <Row key={index} className="mt-2 align-items-center">
                    {/* Location */}
                    <Col md={3}>
                      <Form.Label>Place</Form.Label>
                      <Form.Select
                        value={loc.placeId}
                        onChange={(e) =>
                          handleLocationChange(index, "placeId", e.target.value)
                        }
                      >
                        <option value="">Select</option>
                        {placeList?.map((place) => (
                          <option key={place._id} value={place._id}>
                            {place.name}
                          </option>
                        ))}
                      </Form.Select>

                      {locationErrors[index]?.placeId && (
                        <small className="text-danger">
                          {locationErrors[index].placeId}
                        </small>
                      )}
                    </Col>

                    {/* Price */}

                    <Col md={3}>
                      <Form.Label>Vendor</Form.Label>
                      <Form.Select
                        value={loc.vendor}
                        onChange={(e) =>
                          handleLocationChange(index, "vendor", e.target.value)
                        }
                      >
                        <option value="">Select</option>

                        {vendors?.map((vendor) => (
                          <option key={vendor._id} value={vendor._id}>
                            {vendor.name}
                          </option>
                        ))}
                      </Form.Select>

                      {locationErrors[index]?.vendor && (
                        <small className="text-danger">
                          {locationErrors[index].vendor}
                        </small>
                      )}
                    </Col>
                    <Col md={2}>
                      <Form.Label>Price</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter Price"
                        value={loc.price}
                        onChange={(e) =>
                          handleLocationChange(index, "price", e.target.value)
                        }
                      />
                      {locationErrors[index]?.price && (
                        <small className="text-danger">
                          {locationErrors[index].price}
                        </small>
                      )}
                    </Col>
                    <Col md={3}>
                      <Form.Label>Timings (Optional)</Form.Label>

                      <Select
                        isMulti
                        options={TIME_OPTIONS}
                        value={TIME_OPTIONS.filter((option) =>
                          (loc.timings || []).includes(option.value),
                        )}
                        onChange={(selected) =>
                          handleTimingChange(index, selected)
                        }
                        placeholder="Select timings..."
                      />
                    </Col>

                    {/* Remove Button */}
                    <Col md={1} className="text-end">
                      {locationform.locations.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-outline-danger mt-4"
                          onClick={() => removeLocationRow(index)}
                        >
                          <FaTrash size={12} />
                        </button>
                      )}
                    </Col>
                  </Row>
                ))}
              </Row>

              {/* Buttons */}
              <div className="text-center mt-5 mb-5">
                <button
                  type="button"
                  className="cancel-btn me-3"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>

                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        variant="light"
                        className="me-2"
                      />
                      Updating...
                    </>
                  ) : (
                    "Update"
                  )}
                </button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}
